import { Select, SelectItem } from '@heroui/react';
import _ from 'lodash';
import type { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useFetchStaffMemberCategoriesQuery } from '@/slices/staff-member-category-api-slice.ts';
import type { StaffMemberCategory } from '../types/account.type.ts';
import type { FormControlProps } from '../types/form-props.type.ts';

export interface StaffMemberCategorySelectorFormProps extends FormControlProps {
  accountId: number;
}

const StaffMemberCategorySelector: FC<StaffMemberCategorySelectorFormProps> = ({
  name,
  label = 'Category',
  displayProperty = 'description',
  accountId,
  disabled,
  required,
}) => {
  const { control } = useFormContext();

  const { categories, isLoading } = useFetchStaffMemberCategoriesQuery(null, {
    selectFromResult: ({ data, isLoading }) => ({
      categories: (accountId ? data?.filter(x => x.accountId === accountId) : data) ?? [],
      isLoading,
    }),
  });

  const hasNoCategories = !categories?.length;

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: required ? 'This field is required' : undefined }}
      render={({ field, fieldState }) => (
        <Select
          label={label}
          placeholder={hasNoCategories ? 'None available' : `Select ${label}`}
          variant='bordered'
          isRequired={required}
          isDisabled={disabled || isLoading || hasNoCategories}
          isInvalid={fieldState.invalid}
          errorMessage={fieldState.error?.message}
          selectedKeys={field.value?.id ? new Set([field.value.id.toString()]) : new Set([])}
          items={categories || []}
          onSelectionChange={keys => {
            const selectedId = Array.from(keys)[0];
            if (selectedId) {
              const selectedCategory = categories?.find(
                x => x.id === parseInt(selectedId as string),
              );
              field.onChange(selectedCategory);
            } else {
              field.onChange(null);
            }
          }}
        >
          {(item: StaffMemberCategory) => {
            const categoryLabel = [
              _.get(item, displayProperty),
              item.accountName ? `(${item.accountName})` : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <SelectItem key={item.id.toString()} textValue={categoryLabel}>
                {categoryLabel}
              </SelectItem>
            );
          }}
        </Select>
      )}
    />
  );
};

export default StaffMemberCategorySelector;
